import classNames from "classnames";
import { ReactNode, ComponentPropsWithRef } from "react";

export type TTypographyProps = ComponentPropsWithRef<"span"> & {
  hasTitle?: boolean;
  children?: ReactNode;
  className?: string;
  color?: string;
  block?: boolean;
  variant:
    | "Caption2-Light"
    | "Caption2-Regular"
    | "Caption2-Bold"
    | "Caption1-Light"
    | "Caption1-Regular"
    | "Caption1-Bold"
    | "Caption1-Medium"
    | "Body3-Light"
    | "Body3-Medium"
    | "Body3-Regular"
    | "Body3-Bold"
    | "Body2-Light"
    | "Body2-Regular"
    | "Body2-Bold"
    | "Body2-Medium"
    | "Body1-Light"
    | "Body1-Regular"
    | "Body1-Bold"
    | "Body1-Medium"
    | "BodyPlus1-bold"
    | "BodyPlus2-Bold"
    | "BodyPlus2-medium"
    | "BodyPlus1-Regular"
    | "BodyPlus1-Medium"
    | "BodyPlus2-Regular"
    | "BodyPlus3-Medium"
    | "BodyPlus3-Bold"
    | "BodyPlus3-Regular"
    | "Link"
    | "Button2"
    | "Button1"
    | "Title5-Regular"
    | "Title5-Bold"
    | "Title5-Medium"
    | "Title4-Regular"
    | "Title4-Bold"
    | "Title3-Regular"
    | "Title3-Bold"
    | "Title2-Regular"
    | "Title2-Bold"
    | "Title1-Regular"
    | "Title1-Bold"
    | "H3-Regular"
    | "H3-Bold"
    | "H2-Regular"
    | "H2-Bold"
    | "H1-Regular"
    | "H1-Bold"
    | "Form-Title";
};

const Typography = ({
  children,
  variant,
  className = "",
  color = "text-gray-900",
  block = false,
  hasTitle = false,
  ...rest
}: TTypographyProps) => {
  return (
    <span
      title={typeof children === "string" && hasTitle ? children : ""}
      className={classNames(
        color,
        {
          "leading-3 text-[10px] font-light": variant === "Caption2-Light",
          "text-[10px] leading-3": variant === "Caption2-Regular",
          "text-[10px] leading-3 font-bold": variant === "Caption2-Bold",
          "text-[12px] leading-4 font-light": variant === "Caption1-Light",
          "text-[12px] leading-4": variant === "Caption1-Regular",
          "text-[12px] leading-4 font-bold": variant === "Caption1-Bold",
          "text-[12px] leading-4 font-medium": variant === "Caption1-Medium",
          "text-[13px] leading-4 font-light": variant === "Body3-Light",
          "text-[13px] leading-4": variant === "Body3-Regular",
          "text-[13px] leading-4 font-bold": variant === "Body3-Bold",
          "text-[13px] leading-4 font-medium": variant === "Body3-Medium",
          "text-[14px] leading-4 font-light": variant === "Body2-Light",
          "text-[14px] leading-4": variant === "Body2-Regular",
          "text-[14px] leading-4 font-bold": variant === "Body2-Bold",
          "text-[14px] leading-4 font-medium": variant === "Body2-Medium",
          "text-[16px] leading-5 font-light": variant === "Body1-Light",
          "text-[16px] leading-5": variant === "Body1-Regular",
          "text-[16px] leading-5 font-bold": variant === "Body1-Bold",
          "text-[16px] leading-5 font-medium":
            variant === "Body1-Medium" || variant === "Button2",
          "text-[16px] leading-6 font-bold": variant === "BodyPlus1-bold",
          "text-[14px] leading-[22px] font-bold": variant === "BodyPlus2-Bold",
          "text-[14px] leading-[22px] font-medium":
            variant === "BodyPlus2-medium",
          "text-[16px] leading-6 font-normal": variant === "BodyPlus1-Regular",
          "text-[16px] leading-6 font-medium": variant === "BodyPlus1-Medium",
          "text-[14px] leading-[22px]": variant === "BodyPlus2-Regular",
          "text-[13px] leading-5 font-medium": variant === "BodyPlus3-Medium",
          "text-[13px] leading-5 font-bold": variant === "BodyPlus3-Bold",
          "text-[13px] leading-5": variant === "BodyPlus3-Regular",
          "text-[18px] leading-6": variant === "Link",
          "text-[20px] leading-6 font-medium": variant === "Button1",
          "text-[18px] leading-6 ": variant === "Title5-Regular",
          "text-[18px] leading-6 font-bold": variant === "Title5-Bold",
          "text-[18px] leading-6 font-medium": variant === "Title5-Medium",
          "text-[20px] leading-6": variant === "Title4-Regular",
          "text-[20px] leading-6 font-bold": variant === "Title4-Bold",
          "text-[22px] leading-7": variant === "Title3-Regular",
          "text-[22px] leading-7 font-bold": variant === "Title3-Bold",
          "text-[24px] leading-8": variant === "Title2-Regular",
          "text-[24px] leading-8 font-bold": variant === "Title2-Bold",
          "text-[32px] leading-8": variant === "Title1-Regular",
          "text-[32px] leading-8 font-bold": variant === "Title1-Bold",
          "text-[40px] leading-[48px]": variant === "H3-Regular",
          "text-[40px] leading-[48px] font-bold": variant === "H3-Bold",
          "text-[48px] leading-[64px]": variant === "H2-Regular",
          "text-[48px] leading-[64px] font-bold": variant === "H2-Bold",
          "text-[64px] leading-[80px]": variant === "H1-Regular",
          "text-[64px] leading-[80px] font-bold": variant === "H1-Bold",
          "self-stretch text-gray-800 font-bold leading-6":
            variant === "Form-Title",
        },
        { block: block },
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
};

export default Typography;
